/**
 * Transmisión y tracción: del texto crudo de la base a un valor con el que se
 * pueda filtrar.
 *
 * Las dos son columnas de TEXTO LIBRE, sin catálogo ni FK, y cada automotora
 * escribe como quiere. Medido sobre los 4.376 vehículos de producción:
 *
 *   transmission (15 valores distintos)
 *     automatic 1790 · manual 1564 · Automática 350 · Manual 273 · MANUAL 97
 *     Automatic 90 · MECANICO 52 · AUTOMATICA 38 · AUTOMATICO 28 · Mecanica 16
 *     AUTOMÁTICA 14 · Automatica 11 · MECÁNICA 10 · null 38 · '' 5
 *
 *   traction (8 valores distintos)
 *     null 2773 · 4x2 1026 · 4x4 322 · AWD 129 · 4X2 55 · 4X4 38
 *     TRACCIÓN DELANTERA (FWD) 29 · FWD 4
 *
 * No es sólo mayúsculas: `MECANICO`/`Mecanica`/`MECÁNICA` son la misma caja que
 * `manual` (78 autos), y `TRACCIÓN DELANTERA (FWD)` es `FWD` escrito largo. Un
 * filtro que compare el texto tal cual esconde esos autos sin decir nada, y el
 * usuario lee "no hay stock" en vez de "el dato está escrito distinto".
 *
 * Por eso la normalización vive acá y en un solo lugar: la usan el panel de
 * filtros (para armar las opciones y contarlas), las DOS cadenas de filtrado
 * (/vehicles y el listado del home) y el codec de la URL. Si cada una
 * normalizara por su cuenta, el mismo link daría resultados distintos según la
 * pantalla.
 *
 * Lo que NO hace: tocar la base. Los datos siguen como están; esto es sólo la
 * lectura.
 */

export type TransmissionSlug = 'manual' | 'automatica';
export type TractionSlug = '4x2' | '4x4' | 'awd' | 'fwd';

/** Los slugs son lo que viaja en la URL, así que el orden es el de la UI. */
export const TRANSMISSION_SLUGS: TransmissionSlug[] = ['manual', 'automatica'];
export const TRACTION_SLUGS: TractionSlug[] = ['4x2', '4x4', 'awd', 'fwd'];

/**
 * minúsculas, sin tildes y sin espacios de sobra.
 *
 * `\p{Diacritic}` en vez del rango de combinantes escrito a mano: esos
 * caracteres son invisibles en el archivo y cualquier reencode los rompe sin
 * que se note. Acá lo que se lee es lo que hace.
 */
const plano = (raw: unknown): string =>
  String(raw ?? '')
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim();

/**
 * Texto de la base → slug de transmisión, o `null` si no se puede decidir
 * (vacío, null, o algo que nadie previó).
 *
 * Por prefijo y no por lista cerrada: `automat` cubre automatic/automatica/
 * automatico/automática, y `mecanic` cubre mecanico/mecanica/mecánica. Una
 * lista de las 15 variantes exactas se rompería con la número 16.
 */
export const normalizeTransmission = (raw: unknown): TransmissionSlug | null => {
  const v = plano(raw);
  if (!v) return null;
  if (v.startsWith('manual') || v.startsWith('mecanic')) return 'manual';
  if (v.startsWith('automat')) return 'automatica';
  return null;
};

/**
 * Texto de la base → slug de tracción, o `null`.
 *
 * Por `includes` y no por igualdad: `TRACCIÓN DELANTERA (FWD)` trae la sigla
 * adentro de una frase. 4x2/4x4 van primero porque son los más específicos.
 */
export const normalizeTraction = (raw: unknown): TractionSlug | null => {
  const v = plano(raw);
  if (!v) return null;
  if (v.includes('4x2')) return '4x2';
  if (v.includes('4x4')) return '4x4';
  if (v.includes('awd')) return 'awd';
  if (v.includes('fwd')) return 'fwd';
  return null;
};

/**
 * ¿El vehículo cae en alguno de los slugs elegidos?
 *
 * Sin filtro elegido pasa todo. Con filtro, un vehículo cuyo dato no se pudo
 * normalizar (null/vacío/desconocido) NO pasa: quien filtra "4x4" está pidiendo
 * 4x4, no "4x4 y además los que no sabemos". Los sin dato se siguen viendo en el
 * listado sin filtrar, que es donde corresponde.
 */
export const matchesSpec = <T extends string>(
  seleccionados: string[] | undefined,
  slug: T | null
): boolean => {
  if (!seleccionados || seleccionados.length === 0) return true;
  return slug !== null && seleccionados.includes(slug);
};

export interface SpecCount<T extends string> {
  id: T;
  count: number;
}

/**
 * Cuántos vehículos hay por slug. El panel lo usa para mostrar el número al
 * lado de cada opción y para deshabilitar las que dan 0 — mismo criterio que
 * el filtro Facturable, que ya lo hacía así.
 */
export const countBySpec = <T extends string>(
  vehiculos: unknown[],
  slugs: T[],
  normalizar: (v: unknown) => T | null,
  leer: (vehiculo: never) => unknown
): SpecCount<T>[] => {
  const conteo = new Map<T, number>(slugs.map((s) => [s, 0]));
  for (const v of vehiculos) {
    const slug = normalizar(leer(v as never));
    if (slug !== null && conteo.has(slug)) {
      conteo.set(slug, (conteo.get(slug) ?? 0) + 1);
    }
  }
  return slugs.map((id) => ({ id, count: conteo.get(id) ?? 0 }));
};

/** Conteo de transmisión listo para el panel. */
export const countTransmissions = (
  vehiculos: { transmission?: unknown }[]
): SpecCount<TransmissionSlug>[] =>
  countBySpec(vehiculos, TRANSMISSION_SLUGS, normalizeTransmission, (v: never) =>
    (v as { transmission?: unknown }).transmission
  );

/** Conteo de tracción listo para el panel. */
export const countTractions = (
  vehiculos: { traction?: unknown }[]
): SpecCount<TractionSlug>[] =>
  countBySpec(vehiculos, TRACTION_SLUGS, normalizeTraction, (v: never) =>
    (v as { traction?: unknown }).traction
  );
